import * as React from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { Field } from '@progress/kendo-react-form';
import { messages, editorEventTitle, editorEventStart, editorEventEnd, editorEventDescription, editorEventAllDay, editorEventTimeZone, editorEventSeparateTimeZones, editorValidationRequired, editorValidationStart, editorValidationEnd } from '../messages';
import { Input } from './Input';
import { DatePicker } from './DatePicker';
import { DateTimePicker } from './DateTimePicker';
import { CheckBox } from './CheckBox';
import { RecurrenceEditor } from './RecurrenceEditor';
import { TextArea } from './TextArea';
import { TimezoneEditor } from './TimezoneEditor';
import { DropDownList } from './DropDownList';
import { MultiSelect } from './MultiSelect';
import { labelUserSelect } from './common';
import { useSchedulerPropsContext, useSchedulerFieldsContext } from '../main';
/** @hidden */
export var SchedulerEditor = function (props) {
    var _a = useSchedulerPropsContext(), resources = _a.resources, timezone = _a.timezone;
    var fields = useSchedulerFieldsContext();
    var onChange = props.onChange, valueGetter = props.valueGetter;
    var localizationService = useLocalization();
    var localizeMessage = function (message) {
        return localizationService.toLanguageString(message, messages[message]);
    };
    var requiredValidationMessage = localizeMessage(editorValidationRequired);
    var startValidationMessage = localizeMessage(editorValidationStart);
    var endValidationMessage = localizeMessage(editorValidationEnd);
    var titleMessage = localizeMessage(editorEventTitle);
    var editorEventStartMessage = localizeMessage(editorEventStart);
    var editorEventEndMessage = localizeMessage(editorEventEnd);
    var editorEventDescriptionMessage = localizeMessage(editorEventDescription);
    var editorEventAllDayMessage = localizeMessage(editorEventAllDay);
    var editorEventTimeZoneMessage = localizeMessage(editorEventTimeZone);
    var editorEventSeparateTimeZonesMessage = localizeMessage(editorEventSeparateTimeZones);
    // These are good candidates for form level validation
    var startAfterEndValidator = React.useCallback(function (value, formValueGetter) { return (Boolean(value && formValueGetter(fields.end) && value.getTime() > formValueGetter(fields.end).getTime()) ?
        startValidationMessage :
        undefined); }, [fields, startValidationMessage]);
    var endAfterStartValidator = React.useCallback(function (value, formValueGetter) { return (Boolean(value && formValueGetter(fields.start) && value.getTime() < formValueGetter(fields.start).getTime()) ?
        endValidationMessage :
        undefined); }, [fields, endValidationMessage]);
    var requiredValidator = React.useCallback(function (value) { return (value ? undefined : requiredValidationMessage); }, [requiredValidationMessage]);
    var startValidators = React.useMemo(function () { return [requiredValidator, startAfterEndValidator]; }, [requiredValidator, startAfterEndValidator]);
    var endValidators = React.useMemo(function () { return [requiredValidator, endAfterStartValidator]; }, [requiredValidator, endAfterStartValidator]);
    var getStartTimezone = React.useCallback(function () { return valueGetter(fields.startTimezone) || timezone || ''; }, [timezone, fields, valueGetter]);
    var getEndTimezone = React.useCallback(function () { return valueGetter(fields.endTimezone) || getStartTimezone() || ''; }, [fields, getStartTimezone, valueGetter]);
    var start = valueGetter(fields.start) || new Date();
    var startTimezone = getStartTimezone();
    var endTimezone = getEndTimezone();
    var _b = React.useState(Boolean(valueGetter(fields.startTimezone))), startZoneChecked = _b[0], setStartZoneChecked = _b[1];
    var _c = React.useState(Boolean(valueGetter(fields.endTimezone))), endZoneChecked = _c[0], setEndZoneChecked = _c[1];
    var onStartZoneCheckboxChange = React.useCallback(function (e) {
        if (!e.target.checked) {
            onChange(fields.startTimezone, { value: null });
            onChange(fields.endTimezone, { value: null });
            setEndZoneChecked(e.target.checked);
        }
        setStartZoneChecked(e.target.checked);
    }, [onChange, fields]);
    var onEndZoneCheckboxChange = React.useCallback(function (e) {
        if (!e.target.checked) {
            onChange(fields.endTimezone, { value: null });
        }
        setEndZoneChecked(e.target.checked);
    }, [onChange]);
    return (React.createElement("div", { className: "k-scheduler-edit-form" },
        React.createElement("div", { className: "k-edit-label" },
            React.createElement("label", { htmlFor: "k-edit-title" }, titleMessage)),
        React.createElement("div", { className: "k-edit-field" },
            React.createElement(Field, { component: Input, name: fields.title, id: "k-edit-title", validator: requiredValidator })),
        React.createElement("div", { className: "k-edit-label" },
            React.createElement("label", null, editorEventStartMessage)),
        React.createElement("div", { className: "k-edit-field" },
            React.createElement("div", null,
                valueGetter(fields.isAllDay) && (React.createElement(Field, { component: DatePicker, name: fields.start, validator: startValidators, timezone: startTimezone })),
                !valueGetter(fields.isAllDay) && (React.createElement(Field, { component: DateTimePicker, name: fields.start, validator: startValidators, timezone: startTimezone })),
                "\u00A0",
                React.createElement("div", { style: { verticalAlign: 'top', display: 'inline-block' } },
                    React.createElement("input", { id: 'k-set-timezone', type: 'checkbox', className: 'k-checkbox', onChange: onStartZoneCheckboxChange, checked: startZoneChecked }),
                    React.createElement("label", { className: 'k-checkbox-label', htmlFor: 'k-set-timezone', style: labelUserSelect }, editorEventTimeZoneMessage)))),
        startZoneChecked && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "k-edit-label" }),
            React.createElement("div", { className: "k-edit-field" },
                React.createElement(Field, { component: TimezoneEditor, name: fields.startTimezone, validator: requiredValidator })))),
        React.createElement("div", { className: "k-edit-label" }, editorEventEndMessage),
        React.createElement("div", { className: "k-edit-field" },
            React.createElement("div", null,
                valueGetter(fields.isAllDay) && (React.createElement(Field, { component: DatePicker, name: fields.end, validator: endValidators, timezone: endTimezone })),
                !valueGetter(fields.isAllDay) && (React.createElement(Field, { component: DateTimePicker, name: fields.end, validator: endValidators, timezone: endTimezone })),
                "\u00A0",
                startZoneChecked && (React.createElement("div", { style: { verticalAlign: 'top', display: 'inline-block' } },
                    React.createElement("input", { id: 'k-use-separate', type: 'checkbox', className: 'k-checkbox', onChange: onEndZoneCheckboxChange, checked: endZoneChecked }),
                    React.createElement("label", { className: 'k-checkbox-label', htmlFor: 'k-use-separate' }, editorEventSeparateTimeZonesMessage))))),
        endZoneChecked && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "k-edit-label" }),
            React.createElement("div", { className: "k-edit-field" },
                React.createElement(Field, { component: TimezoneEditor, name: fields.endTimezone, validator: requiredValidator })))),
        React.createElement("div", { className: "k-edit-label" }),
        React.createElement("div", { className: "k-edit-field" },
            React.createElement(Field, { component: CheckBox, name: fields.isAllDay, id: 'k-is-allday-chkbox', label: editorEventAllDayMessage })),
        React.createElement(Field, { component: RecurrenceEditor, name: fields.recurrenceRule, start: start, timezone: startTimezone }),
        React.createElement("div", { className: "k-edit-label" },
            React.createElement("label", null, editorEventDescriptionMessage)),
        React.createElement("div", { className: "k-edit-field" },
            React.createElement(Field, { component: TextArea, name: fields.description, id: 'k-event-description', style: { width: '100%' } })),
        generateResourceEditors(resources)));
};
var generateResourceEditors = function (resources) {
    if (!resources) {
        return null;
    }
    return resources.map(function (resource) {
        return (React.createElement("div", { key: resource.field },
            React.createElement("div", { className: "k-edit-label" },
                React.createElement("label", null, resource.name)),
            React.createElement("div", { className: "k-edit-field" },
                resource.multiple && (React.createElement(Field, { name: resource.field, component: MultiSelect, data: resource.data, textField: resource.textField, dataItemKey: resource.valueField, colorField: resource.colorField })),
                !resource.multiple && (React.createElement(Field, { name: resource.field, component: DropDownList, data: resource.data, textField: resource.textField, dataItemKey: resource.valueField, colorField: resource.colorField })))));
    });
};
//# sourceMappingURL=SchedulerEditor.js.map